import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_DATE_FORMAT,
  effectiveDateLocale,
  normaliseDateFormat,
  type DateFormatPref,
} from "@heraion/shared";

// The date-format preference (#390) and the one hook that turns it into the
// locale a date is written in. Stored per DEVICE next to the language (its
// default is the device's region, so it cannot ride the profile). The host
// app supplies the storage (mobile: AsyncStorage, web: localStorage) and the
// device locale; this owns the state and the rule.
//
// Without a provider (tests, the sign-in door) the context is `auto` with no
// device locale, which resolves to the UI language: the pre-#390 behaviour.

export type DateFormatStorage = {
  read: () => Promise<string | null> | string | null;
  write: (value: DateFormatPref) => void | Promise<void>;
};

type Value = {
  pref: DateFormatPref;
  setPref: (next: DateFormatPref) => void;
  deviceLocale: string | null;
};

const Ctx = createContext<Value>({
  pref: DEFAULT_DATE_FORMAT,
  setPref: () => {},
  deviceLocale: null,
});

const readNow = (storage: DateFormatStorage): ReturnType<DateFormatStorage["read"]> => {
  try {
    return storage.read();
  } catch {
    return null;
  }
};

const isPending = (raw: ReturnType<DateFormatStorage["read"]>): raw is Promise<string | null> =>
  typeof (raw as Promise<string | null> | null)?.then === "function";

export function DateFormatProvider({
  storage,
  deviceLocale,
  children,
}: {
  storage: DateFormatStorage;
  deviceLocale: string | null;
  children: ReactNode;
}) {
  // A synchronous store (web localStorage) is read before the first paint, so
  // no date flashes in the wrong order; an async one (mobile) lands after.
  const firstRead = useRef<ReturnType<DateFormatStorage["read"]> | undefined>(undefined);
  const [pref, setPrefState] = useState<DateFormatPref>(() => {
    const raw = readNow(storage);
    firstRead.current = raw;
    return isPending(raw) ? DEFAULT_DATE_FORMAT : normaliseDateFormat(raw);
  });

  useEffect(() => {
    const raw = firstRead.current !== undefined ? firstRead.current : readNow(storage);
    firstRead.current = undefined;
    if (!isPending(raw)) {
      setPrefState(normaliseDateFormat(raw));
      return;
    }
    let live = true;
    raw
      .then((value) => {
        if (live) setPrefState(normaliseDateFormat(value));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [storage]);

  const setPref = useCallback(
    (next: DateFormatPref) => {
      setPrefState(next);
      // A failed write keeps the choice for this session, as the language does.
      void Promise.resolve(storage.write(next)).catch(() => {});
    },
    [storage],
  );

  const value = useMemo(() => ({ pref, setPref, deviceLocale }), [pref, setPref, deviceLocale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useDateFormatPref = (): Value => useContext(Ctx);

/** The locale every date is written in: the language + the date-format preference + the device region. */
export function useDateLocale(): string {
  // The optional chain is for hosts and test doubles whose useTranslation
  // carries no i18n instance: they read the date in English.
  const i18n = useTranslation().i18n as { language?: string } | undefined;
  const { pref, deviceLocale } = useContext(Ctx);
  return effectiveDateLocale(i18n?.language || "en", pref, deviceLocale);
}
